import { useState, useMemo } from 'react';
import { Download } from 'lucide-react';
import type { ErlangCInputs } from '../types/erlang';
import { calculateErlangC, staffingCurve } from '../utils/erlangEngine';
import { generateErlangCReport, downloadReport } from '../utils/reportGenerator';
import MetricCard from './MetricCard';
import StaffingCurve from './StaffingCurve';

const TOOLTIPS = {
  calls: 'Total calls offered during this interval. For a 30-minute interval, this is half-hourly volume.',
  aht: 'Average Handle Time — total talk + hold + after-call work time in seconds.',
  interval: 'Length of the planning interval in minutes. Standard WFM uses 15 or 30 minutes.',
  targetSL: 'Target percentage of calls answered within the target answer time (e.g., 80% in 20 seconds).',
  targetTime: 'Maximum acceptable wait time in seconds for the service level calculation.',
  shrinkage: 'Percentage of scheduled time agents are unavailable (breaks, training, meetings, absenteeism).',
  cost: 'Fully loaded cost per agent per hour (salary + benefits + overhead).',
};

export default function ErlangCTab() {
  const [inputs, setInputs] = useState<ErlangCInputs>({
    callsPerInterval: 350,
    ahtSeconds: 240,
    intervalMinutes: 30,
    targetSLPercent: 80,
    targetAnswerTime: 20,
    shrinkagePercent: 30,
    costPerAgentHour: 25,
  });

  const results = useMemo(() => calculateErlangC(inputs), [inputs]);

  const curveData = useMemo(
    () =>
      staffingCurve(
        results.trafficIntensity,
        inputs.targetAnswerTime,
        inputs.ahtSeconds,
        inputs.targetSLPercent / 100
      ),
    [results.trafficIntensity, inputs.targetAnswerTime, inputs.ahtSeconds, inputs.targetSLPercent]
  );

  const update = (field: keyof ErlangCInputs, value: string) => {
    const num = parseFloat(value);
    if (!isNaN(num) && num >= 0) {
      setInputs((prev) => ({ ...prev, [field]: num }));
    }
  };

  const handleDownload = () => {
    const report = generateErlangCReport(inputs, results);
    downloadReport(report, 'erlang-c-report.txt');
  };

  const occWarning = results.occupancy > 0.88;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Inputs */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider">Inputs</h3>
          <InputField label="Calls per Interval" value={inputs.callsPerInterval} onChange={(v) => update('callsPerInterval', v)} tooltip={TOOLTIPS.calls} />
          <InputField label="AHT (seconds)" value={inputs.ahtSeconds} onChange={(v) => update('ahtSeconds', v)} tooltip={TOOLTIPS.aht} />
          <InputField label="Interval Length (min)" value={inputs.intervalMinutes} onChange={(v) => update('intervalMinutes', v)} tooltip={TOOLTIPS.interval} />
          <InputField label="Target SL %" value={inputs.targetSLPercent} onChange={(v) => update('targetSLPercent', v)} tooltip={TOOLTIPS.targetSL} />
          <InputField label="Target Answer Time (s)" value={inputs.targetAnswerTime} onChange={(v) => update('targetAnswerTime', v)} tooltip={TOOLTIPS.targetTime} />
          <InputField label="Shrinkage %" value={inputs.shrinkagePercent} onChange={(v) => update('shrinkagePercent', v)} tooltip={TOOLTIPS.shrinkage} />
          <InputField label="Cost per Agent $/hr" value={inputs.costPerAgentHour} onChange={(v) => update('costPerAgentHour', v)} tooltip={TOOLTIPS.cost} />
        </div>

        {/* Results */}
        <div className="lg:col-span-2 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider">Results</h3>
            <button
              onClick={handleDownload}
              className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-white border border-gray-700 hover:border-gray-500 rounded px-3 py-1.5 transition-colors"
            >
              <Download size={14} /> Download Report
            </button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <MetricCard label="Traffic Intensity" value={results.trafficIntensity.toFixed(2)} sublabel="Erlangs" tooltip="Offered load = Calls x AHT / Interval. Represents the minimum servers needed if there were no waiting." />
            <MetricCard label="Raw Agents" value={results.rawAgents} highlight tooltip="Minimum agents needed to meet your service level target." />
            <MetricCard label="Rostered Agents" value={results.rosteredAgents} highlight tooltip="Raw agents adjusted for shrinkage. This is how many you need to schedule." />
            <MetricCard label="Service Level" value={(results.serviceLevel * 100).toFixed(1) + '%'} tooltip="Percentage of calls answered within the target answer time." />
            <MetricCard label="ASA" value={results.asa.toFixed(1) + 's'} tooltip="Average Speed of Answer — mean wait time for all callers." />
            <MetricCard label="P(Wait)" value={(results.probabilityWait * 100).toFixed(1) + '%'} tooltip="Probability a caller must wait in queue (not answered immediately)." />
            <MetricCard
              label="Occupancy"
              value={(results.occupancy * 100).toFixed(1) + '%'}
              warning={occWarning}
              tooltip="Agent utilization = Traffic Intensity / Agents. This is a result of your workload and SL target, not an input. Above ~88% signals burnout risk."
            />
            <MetricCard label="Immediate Answer" value={(results.immediateAnswer * 100).toFixed(1) + '%'} tooltip="Percentage of callers answered without any wait." />
            <MetricCard label="Avg Queue Length" value={results.avgQueueLength.toFixed(2)} tooltip="Average number of callers waiting in queue at any moment." />
            <MetricCard label="Cost / Interval" value={'$' + results.costPerInterval.toFixed(2)} tooltip="Total agent cost for one interval (rostered agents x interval hours x rate)." />
            <MetricCard label="Cost / Hour" value={'$' + results.costPerHour.toFixed(2)} tooltip="Total hourly cost for all rostered agents." />
          </div>

          {occWarning && (
            <div className="text-sm text-cyan-400 bg-cyan-950/30 border border-cyan-700/30 rounded-lg px-4 py-3">
              Occupancy is {(results.occupancy * 100).toFixed(1)}% — above 88% signals burnout risk. This is a direct function of your workload and SL target. To lower it, either reduce volume, lower AHT, or tighten the SL target (which forces more agents).
            </div>
          )}

          <StaffingCurve data={curveData} targetSL={inputs.targetSLPercent / 100} />
        </div>
      </div>
    </div>
  );
}

// ── Shared Input Component ──

function InputField({
  label,
  value,
  onChange,
  tooltip,
}: {
  label: string;
  value: number;
  onChange: (v: string) => void;
  tooltip?: string;
}) {
  const [showTip, setShowTip] = useState(false);

  return (
    <div className="relative">
      <div className="flex items-center justify-between mb-1">
        <label className="text-xs text-gray-400">{label}</label>
        {tooltip && (
          <div className="relative">
            <span
              className="text-gray-500 hover:text-gray-300 cursor-help text-xs"
              onMouseEnter={() => setShowTip(true)}
              onMouseLeave={() => setShowTip(false)}
            >
              &#9432;
            </span>
            {showTip && (
              <div className="absolute right-0 top-5 z-50 w-52 rounded bg-gray-800 border border-gray-600 p-2 text-xs text-gray-300 shadow-lg">
                {tooltip}
              </div>
            )}
          </div>
        )}
      </div>
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-navy-900/50 border border-gray-700/50 rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500 transition-colors"
      />
    </div>
  );
}

export { InputField };
